import { ApiService } from "../utils/ApiService.js";
import { LcapiiService } from "./LcapiiService.js";

// Auditoría de los códigos generados.
//
// La app anterior hacía el POST y, si fallaba, solo dejaba un console.error: en
// una caída de internet se generaban códigos que no quedaban registrados en
// ningún lado. Acá lo que no se puede enviar se encola y se despacha al volver
// la conexión.

const COLA_KEY = "registros_pendientes";
const MAX_PENDIENTES = 200;

const leerCola = () => {
  try {
    const raw = localStorage.getItem(COLA_KEY);
    const cola = raw ? JSON.parse(raw) : [];
    return Array.isArray(cola) ? cola : [];
  } catch {
    return [];
  }
};

const guardarCola = (cola) => {
  try {
    localStorage.setItem(COLA_KEY, JSON.stringify(cola.slice(-MAX_PENDIENTES)));
  } catch {
    // si no hay espacio, se pierde el más viejo antes que romper la operación
  }
};

const esFallaDeRed = (error) => !error?.status;

export const RegistroService = {
  /**
   * Registra un código. Nunca lanza: que la auditoría falle no puede impedir
   * que el usuario reciba el código que ya calculó.
   * Devuelve "enviado" | "encolado" | "descartado".
   */
  async registrar(payload) {
    try {
      const token = await LcapiiService.getAccessToken();
      await ApiService.post("registrar", payload, token);
      return "enviado";
    } catch (error) {
      if (esFallaDeRed(error)) {
        const cola = leerCola();
        cola.push({ payload, intentos: 0, encoladoEn: Date.now() });
        guardarCola(cola);
        return "encolado";
      }
      // Un rechazo del servidor (400 por datos inválidos, 401 por sesión caída)
      // no mejora reintentándolo: encolarlo sería reintentar para siempre.
      console.error("El registro fue rechazado por la API:", error);
      return "descartado";
    }
  },

  pendientes() {
    return leerCola().length;
  },

  /** Despacha lo encolado. Se corta al primer fallo de red para no vaciar en vano. */
  async despacharPendientes() {
    const cola = leerCola();
    if (cola.length === 0) return { enviados: 0, pendientes: 0 };

    const token = await LcapiiService.getAccessToken();
    if (!token) return { enviados: 0, pendientes: cola.length };

    let enviados = 0;
    while (cola.length > 0) {
      const item = cola[0];
      try {
        await ApiService.post("registrar", item.payload, token);
        cola.shift();
        enviados++;
      } catch (error) {
        if (esFallaDeRed(error)) break;
        // rechazado por el servidor: se descarta para que no trabe la cola
        console.error("Registro pendiente rechazado, se descarta:", error);
        cola.shift();
      }
    }

    guardarCola(cola);
    return { enviados, pendientes: cola.length };
  },

  /** Reintenta apenas vuelve la conexión. */
  escucharConexion() {
    window.addEventListener("online", () => {
      RegistroService.despacharPendientes().catch(() => {});
    });
  },
};
